import { getFieldValueLabel } from './configurationAdminUtils';
import { buildPartMatchRulePayload } from './partAutoSelectUtils';

export const PART_AUTO_SELECT_BULK_HEADERS = [
  'Id',
  'PartId',
  'MasterStationId',
  'RuleGroupId',
  'ConditionType',
  'RequiredFieldValueId',
  'NumericFieldId',
  'MinValue',
  'MaxValue',
  'Quantity',
];

const asText = (value) => String(value ?? '').trim();
const asNullableNumber = (value) => asText(value) === '' ? null : Number(value);
const normalizeHeader = (value) => asText(value).toLowerCase().replace(/[^a-z0-9]/g, '');
const HEADER_BY_KEY = new Map(PART_AUTO_SELECT_BULK_HEADERS.map((header) => [normalizeHeader(header), header]));

let bulkPartRuleRowKey = 0;
const nextClientKey = () => `part-auto-bulk-${++bulkPartRuleRowKey}`;

const isBlankMatrixRow = (row = []) => row.every((value) => asText(value) === '');

export const normalizePartAutoConditionType = (value, requiredValueId, numericFieldId) => {
  const normalized = asText(value).toLowerCase().replace(/[^a-z]/g, '');
  if (['selectedvalue', 'value', 'requiredvalue', 'dropdown'].includes(normalized)) return 'value';
  if (['numeric', 'numericrange', 'range', 'number'].includes(normalized)) return 'numeric';
  if (['always', 'alwaysselected', 'default', 'unconditional'].includes(normalized)) return 'default';
  if (!normalized && asText(requiredValueId)) return 'value';
  if (!normalized && asText(numericFieldId)) return 'numeric';
  if (!normalized) return 'default';
  return normalized;
};

export const getPartAutoBulkScopeRules = (rules = [], assignedPartIds = [], stationId = null) => {
  const allowedIds = assignedPartIds instanceof Set ? assignedPartIds : new Set(assignedPartIds);
  const numericStationId = Number(stationId) || 0;
  return rules.filter((rule) => (
    allowedIds.has(Number(rule.partId))
    && (!numericStationId || Number(rule.stationId) === numericStationId)
  ));
};

export const createPartAutoBulkRows = (rules = []) => [...rules]
  .sort((left, right) => (
    Number(left.stationId) - Number(right.stationId)
    || Number(left.partId) - Number(right.partId)
    || Number(left.ruleGroupId) - Number(right.ruleGroupId)
    || Number(left.id) - Number(right.id)
  ))
  .map((rule) => ({
    clientKey: nextClientKey(),
    id: rule.id,
    partId: rule.partId,
    stationId: rule.stationId,
    ruleGroupId: rule.ruleGroupId,
    conditionType: rule.requiredValueId ? 'value' : rule.fieldId ? 'numeric' : 'default',
    requiredValueId: rule.requiredValueId ?? '',
    fieldId: rule.fieldId ?? '',
    minValue: rule.minValue ?? '',
    maxValue: rule.maxValue ?? '',
    quantity: rule.quantity ?? 0,
    isExisting: true,
  }));

export const createBlankPartAutoBulkRow = ({
  partId = '',
  stationId = '',
  ruleGroupId = '',
  conditionType = 'value',
  requiredValueId = '',
  fieldId = '',
  minValue = '',
  maxValue = '',
  quantity = 1,
} = {}) => ({
  clientKey: nextClientKey(),
  id: '',
  partId,
  stationId,
  ruleGroupId,
  conditionType,
  requiredValueId: conditionType === 'value' ? requiredValueId : '',
  fieldId: conditionType === 'numeric' ? fieldId : '',
  minValue: conditionType === 'numeric' ? minValue : '',
  maxValue: conditionType === 'numeric' ? maxValue : '',
  quantity,
  isExisting: false,
});

export const getNextPartAutoBulkRuleGroupId = (allRules = [], stagedRows = []) => Math.max(
  0,
  ...allRules.map((rule) => Number(rule.ruleGroupId) || 0),
  ...stagedRows.map((row) => Number(row.ruleGroupId) || 0)
) + 1;

export const parsePartAutoBulkImportMatrix = (matrix = []) => {
  const firstPopulatedRowIndex = matrix.findIndex((row) => !isBlankMatrixRow(row));
  if (firstPopulatedRowIndex < 0) throw new Error('The uploaded table is empty.');

  const rawHeaders = matrix[firstPopulatedRowIndex];
  const mappedHeaders = rawHeaders.map((header) => HEADER_BY_KEY.get(normalizeHeader(header)) || '');
  const unknownHeaders = rawHeaders.filter((header, index) => asText(header) && !mappedHeaders[index]);
  if (unknownHeaders.length > 0) throw new Error(`Unknown column${unknownHeaders.length === 1 ? '' : 's'}: ${unknownHeaders.join(', ')}.`);
  for (const requiredHeader of ['PartId', 'MasterStationId', 'RuleGroupId', 'ConditionType', 'Quantity']) {
    if (!mappedHeaders.includes(requiredHeader)) throw new Error(`The table must include a ${requiredHeader} column.`);
  }

  const records = matrix
    .slice(firstPopulatedRowIndex + 1)
    .filter((row) => !isBlankMatrixRow(row))
    .map((row) => Object.fromEntries(mappedHeaders
      .map((header, index) => [header, row[index]])
      .filter(([header]) => header)));
  if (records.length === 0) throw new Error('The table does not contain any Part Auto Pick rule rows.');

  return records.map((record) => {
    const conditionType = normalizePartAutoConditionType(
      record.ConditionType,
      record.RequiredFieldValueId,
      record.NumericFieldId
    );
    return {
      clientKey: nextClientKey(),
      id: asText(record.Id),
      partId: asText(record.PartId),
      stationId: asText(record.MasterStationId),
      ruleGroupId: asText(record.RuleGroupId),
      conditionType,
      requiredValueId: conditionType === 'value' ? asText(record.RequiredFieldValueId) : '',
      fieldId: conditionType === 'numeric' ? asText(record.NumericFieldId) : '',
      minValue: conditionType === 'numeric' ? asText(record.MinValue) : '',
      maxValue: conditionType === 'numeric' ? asText(record.MaxValue) : '',
      quantity: asText(record.Quantity),
      isExisting: false,
    };
  });
};

export const mergeImportedPartAutoBulkRows = (currentRows = [], importedRows = []) => {
  const importedIds = new Set();
  importedRows.forEach((row) => {
    if (!asText(row.id)) return;
    const id = Number(row.id);
    if (importedIds.has(id)) throw new Error(`The import contains duplicate ID ${id}.`);
    importedIds.add(id);
  });

  const nextRows = [...currentRows];
  importedRows.forEach((row) => {
    const numericId = Number(row.id);
    const existingIndex = row.id === '' ? -1 : nextRows.findIndex((candidate) => Number(candidate.id) === numericId);
    if (existingIndex >= 0) {
      nextRows[existingIndex] = { ...row, clientKey: nextRows[existingIndex].clientKey, isExisting: true };
    } else {
      nextRows.push(row);
    }
  });
  return nextRows;
};

const toCondition = (row) => ({
  id: row.id,
  type: row.conditionType,
  requiredValueId: row.requiredValueId,
  fieldId: row.fieldId,
  minValue: row.minValue,
  maxValue: row.maxValue,
  quantity: row.quantity,
});

const comparableRule = (row) => ({
  partId: Number(row.partId),
  stationId: Number(row.stationId) || 0,
  ruleGroupId: Number(row.ruleGroupId),
  quantity: Number(row.quantity) || 0,
  requiredValueId: row.requiredValueId === null || row.requiredValueId === undefined || row.requiredValueId === '' ? null : Number(row.requiredValueId),
  fieldId: row.fieldId === null || row.fieldId === undefined || row.fieldId === '' ? null : Number(row.fieldId),
  minValue: row.minValue === null || row.minValue === undefined || row.minValue === '' ? null : Number(row.minValue),
  maxValue: row.maxValue === null || row.maxValue === undefined || row.maxValue === '' ? null : Number(row.maxValue),
});

const payloadToComparableRule = (payload) => comparableRule({
  partId: payload.PartId,
  stationId: payload.MasterStationId,
  ruleGroupId: payload.RuleGroupId,
  quantity: payload.Quantity,
  requiredValueId: payload.RequiredFieldValueId,
  fieldId: payload.NumericFieldId,
  minValue: payload.MinValue,
  maxValue: payload.MaxValue,
});

const payloadsMatch = (left, right) => JSON.stringify(payloadToComparableRule(left)) === JSON.stringify(payloadToComparableRule(right));

const addError = (errorsByKey, row, message) => {
  errorsByKey[row.clientKey] = [...(errorsByKey[row.clientKey] || []), message];
};

export const analyzePartAutoBulkRows = ({
  rows = [],
  baselineRules = [],
  allRules = [],
  assignedParts = [],
  assignedFieldValues = [],
  numericFields = [],
  stationId = null,
}) => {
  const numericStationId = Number(stationId) || 0;
  const baselineById = new Map(baselineRules.map((rule) => [Number(rule.id), rule]));
  const baselineGroupPairs = new Set(baselineRules.map((rule) => (
    `${Number(rule.stationId) || 0}:${Number(rule.partId)}:${Number(rule.ruleGroupId)}`
  )));
  const groupPartIds = [...allRules, ...rows].reduce((result, rule) => {
    const ruleGroupId = Number(rule.ruleGroupId);
    const partId = Number(rule.partId);
    const rowStationId = Number(rule.stationId) || 0;
    if (!Number.isInteger(ruleGroupId) || ruleGroupId <= 0 || !Number.isInteger(partId) || partId <= 0) return result;
    const key = `${rowStationId}:${ruleGroupId}`;
    if (!result.has(key)) result.set(key, new Set());
    result.get(key).add(partId);
    return result;
  }, new Map());
  const assignedPartIds = new Set(assignedParts.map((part) => Number(part.id)));
  const assignedValueIds = new Set(assignedFieldValues.map((value) => Number(value.id)));
  const numericFieldIds = new Set(numericFields.map((field) => Number(field.id)));
  const errorsByKey = {};
  const ids = new Map();
  const groups = new Map();

  rows.forEach((row, index) => {
    const hasId = asText(row.id) !== '';
    const id = Number(row.id);
    const partId = Number(row.partId);
    const rowStationId = Number(row.stationId);
    const ruleGroupId = Number(row.ruleGroupId);
    const requiredValueId = asNullableNumber(row.requiredValueId);
    const fieldId = asNullableNumber(row.fieldId);
    const minValue = asNullableNumber(row.minValue);
    const maxValue = asNullableNumber(row.maxValue);
    const quantity = Number(row.quantity);

    if (hasId && (!Number.isInteger(id) || id <= 0)) addError(errorsByKey, row, 'ID must be a positive whole number or blank for a new rule row.');
    if (hasId && !baselineById.has(id)) addError(errorsByKey, row, `ID ${row.id} is not an existing rule row in this editor.`);
    if (hasId && ids.has(id)) addError(errorsByKey, row, `ID ${row.id} is repeated in rows ${ids.get(id)} and ${index + 1}.`);
    if (hasId) ids.set(id, index + 1);

    if (!Number.isInteger(partId) || !assignedPartIds.has(partId)) addError(errorsByKey, row, 'PartId must be a part assigned to the selected station.');
    if (!Number.isInteger(rowStationId) || rowStationId !== numericStationId) addError(errorsByKey, row, 'MasterStationId must match the selected station.');
    if (!Number.isInteger(ruleGroupId) || ruleGroupId <= 0) addError(errorsByKey, row, 'RuleGroupId must be a positive whole number.');
    const groupPair = `${rowStationId}:${partId}:${ruleGroupId}`;
    if (
      Number.isInteger(ruleGroupId)
      && ruleGroupId > 0
      && Number.isInteger(partId)
      && partId > 0
      && Number.isInteger(rowStationId)
      && rowStationId > 0
      && !baselineGroupPairs.has(groupPair)
      && [...(groupPartIds.get(`${rowStationId}:${ruleGroupId}`) || [])].some((ownerPartId) => ownerPartId !== partId)
    ) {
      addError(errorsByKey, row, `RuleGroupId ${ruleGroupId} is already used by another part for this station. New groups must use the next available station-scoped RuleGroupId.`);
    }
    if (asText(row.quantity) === '' || !Number.isInteger(quantity) || quantity < 0) addError(errorsByKey, row, 'Quantity must be a whole number of 0 or more.');
    if (!['value', 'numeric', 'default'].includes(row.conditionType)) addError(errorsByKey, row, 'ConditionType must be SelectedValue, Numeric, or Always.');

    if (row.conditionType === 'value') {
      if (!Number.isInteger(requiredValueId) || !assignedValueIds.has(requiredValueId)) addError(errorsByKey, row, 'Choose a selected value assigned to this station in Step 5.');
      if (fieldId !== null || minValue !== null || maxValue !== null) addError(errorsByKey, row, 'A SelectedValue condition cannot also contain a numeric field or range.');
    } else if (row.conditionType === 'numeric') {
      if (!Number.isInteger(fieldId) || !numericFieldIds.has(fieldId)) addError(errorsByKey, row, 'Choose a numeric field assigned to this station in Step 5.');
      if (requiredValueId !== null) addError(errorsByKey, row, 'A Numeric condition cannot also contain RequiredFieldValueId.');
      if (minValue !== null && !Number.isFinite(minValue)) addError(errorsByKey, row, 'MinValue must be numeric or blank.');
      if (maxValue !== null && !Number.isFinite(maxValue)) addError(errorsByKey, row, 'MaxValue must be numeric or blank.');
      if (Number.isFinite(minValue) && Number.isFinite(maxValue) && minValue > maxValue) addError(errorsByKey, row, 'MinValue cannot exceed MaxValue.');
    } else if (row.conditionType === 'default' && (requiredValueId !== null || fieldId !== null || minValue !== null || maxValue !== null)) {
      addError(errorsByKey, row, 'An Always condition cannot contain a selected value, numeric field, or range.');
    }

    if (Number.isInteger(ruleGroupId) && ruleGroupId > 0) {
      const groupKey = `${rowStationId}:${partId}:${ruleGroupId}`;
      if (!groups.has(groupKey)) groups.set(groupKey, { stationId: rowStationId, partId, ruleGroupId, rows: [] });
      groups.get(groupKey).rows.push(row);
    }
  });

  groups.forEach(({ rows: groupRows, ruleGroupId: groupId }) => {
    const quantities = new Set(groupRows.map((row) => asText(row.quantity)));
    if (quantities.size !== 1) groupRows.forEach((row) => addError(errorsByKey, row, `Quantity must match across every AND row in rule group ${groupId}.`));
    const defaultRows = groupRows.filter((row) => row.conditionType === 'default');
    if (defaultRows.length > 0 && groupRows.length !== 1) groupRows.forEach((row) => addError(errorsByKey, row, `Always must be the only row in rule group ${groupId}.`));

    const conditions = new Map();
    groupRows.forEach((row, index) => {
      const conditionKey = row.conditionType === 'value'
        ? `value:${Number(row.requiredValueId)}`
        : row.conditionType === 'numeric'
          ? `numeric:${Number(row.fieldId)}:${asText(row.minValue)}:${asText(row.maxValue)}`
          : 'default';
      if (conditions.has(conditionKey)) addError(errorsByKey, row, `Duplicates the condition in AND row ${conditions.get(conditionKey)} of rule group ${groupId}.`);
      else conditions.set(conditionKey, index + 1);
    });
  });

  const operations = [];
  let unchangedCount = 0;
  rows.forEach((row) => {
    if (errorsByKey[row.clientKey]) return;
    const hasId = asText(row.id) !== '';
    const payload = buildPartMatchRulePayload(row.partId, row.ruleGroupId, toCondition(row), 'api', stationId);
    if (!hasId) {
      operations.push({ method: 'POST', payload, clientKey: row.clientKey });
      return;
    }
    const baseline = baselineById.get(Number(row.id));
    const baselinePayload = buildPartMatchRulePayload(
      baseline.partId,
      baseline.ruleGroupId,
      {
        id: baseline.id,
        type: baseline.requiredValueId ? 'value' : baseline.fieldId ? 'numeric' : 'default',
        requiredValueId: baseline.requiredValueId,
        fieldId: baseline.fieldId,
        minValue: baseline.minValue,
        maxValue: baseline.maxValue,
        quantity: baseline.quantity,
      },
      'api',
      baseline.stationId
    );
    if (payloadsMatch(payload, baselinePayload)) unchangedCount += 1;
    else operations.push({ method: 'PUT', payload, clientKey: row.clientKey });
  });
  operations.sort((left, right) => (
    Number(left.payload.PartId) - Number(right.payload.PartId)
    || Number(left.payload.MasterStationId || 0) - Number(right.payload.MasterStationId || 0)
    || Number(left.payload.RuleGroupId) - Number(right.payload.RuleGroupId)
  ));

  return {
    errorsByKey,
    errorCount: Object.values(errorsByKey).reduce((total, errors) => total + errors.length, 0),
    operations,
    createCount: operations.filter((operation) => operation.method === 'POST').length,
    updateCount: operations.filter((operation) => operation.method === 'PUT').length,
    unchangedCount,
    groupCount: groups.size,
    configuredPartCount: new Set(rows.map((row) => Number(row.partId)).filter((id) => id > 0)).size,
  };
};

export const getPartAutoBulkSnapshotConflict = (baselineRules = [], currentRules = []) => {
  const normalize = (rules) => rules.map((rule) => ({ id: Number(rule.id), ...comparableRule(rule) }))
    .sort((left, right) => left.id - right.id);
  return JSON.stringify(normalize(baselineRules)) === JSON.stringify(normalize(currentRules))
    ? ''
    : 'Part Auto Pick rules changed on the server after this bulk editor was opened. Close and reopen it before saving so those changes are not overwritten.';
};

export const getPartAutoBulkTemplateRows = (rules = []) => [
  PART_AUTO_SELECT_BULK_HEADERS,
  ...rules.map((rule) => [
    rule.id,
    rule.partId,
    rule.stationId,
    rule.ruleGroupId,
    rule.requiredValueId ? 'SelectedValue' : rule.fieldId ? 'Numeric' : 'Always',
    rule.requiredValueId ?? '',
    rule.fieldId ?? '',
    rule.minValue ?? '',
    rule.maxValue ?? '',
    rule.quantity ?? 0,
  ]),
];

export const getPartAutoValueOptionLabel = (value, field) => (
  `${field?.fieldName || `Field ${value?.configFieldId || '?'}`} — ${value ? getFieldValueLabel(value) : 'Unknown value'}`
);
